import styled from "@emotion/styled";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { ChronoUnit, LocalTime } from "@js-joda/core";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { DayOfWeek } from "../messages";
import { STRINGS } from "../strings";
import { globalize, translate } from "../translate";
import { formatDayOfWeekAndTimeShort, formatTimes } from "../utils/dateUtils";
import AvailableFlexibleShiftLeading from "./AvailableFlexibleShiftLeading";
import AvailableFlexibleTimeRangeSlider from "./AvailableFlexibleTimeRangeSlider";
import { AppListItem } from "./Generic/AppList";
import Flex from "./Generic/Flex";

interface Props {
  day: DayOfWeek;
  isChecked: boolean | null;
  isDisabled: boolean;
  durations: {
    numberOfHours: number;
    isSelected: boolean | null;
    isDisabled: boolean;
  }[];
  endTime?: LocalTime;
  startTime: LocalTime;
  areTimesFlexible: boolean;
  isSelectedTimeInAvailabilityRange: boolean;
  isValidRange: boolean;
  showTimesLabel: boolean;
  startsToday: boolean;
  knownDurationInHours: number | null;
  minStartTime: LocalTime;
  durationInHours: number | null;
  isDurationSelected: boolean;
  isOvernight: boolean;
  firstDuration: number;
  onToggleDay: (day: DayOfWeek) => void;
  onSelectDuration: (day: DayOfWeek, duration: number) => void;
  onChangeStartTime: (day: DayOfWeek, value: number | number[] | undefined | null) => void;
}

function AvailableFlexibleShift(props: Props) {
  const [activateSliderHelpAnimation, setActivateSliderHelpAnimation] =
    React.useState<boolean>(true);
  const [animate] = useAutoAnimate<HTMLDivElement>();

  const handleChangeStartTime = (day: DayOfWeek, value: number | number[] | null | undefined) => {
    setActivateSliderHelpAnimation(false);
    props.onChangeStartTime(day, value);
  };

  const {
    showTimesLabel,
    startsToday,
    knownDurationInHours,
    minStartTime,
    durationInHours,
    firstDuration,
    isOvernight,
    isDurationSelected,
  } = props;

  return (
    <AppListItem
      title={
        <DayBlocksContainer>
          <DayBlockHeader
            isDisabled={props.isDisabled}
            onClick={() => props.onToggleDay(props.day)}
          >
            <AvailableFlexibleShiftLeading
              isChecked={props.isChecked}
              onClick={() => props.onToggleDay(props.day)}
            />
            <Flex ref={animate} direction="column" flex={1} alignItems="stretch">
              <DayOfWeekText>{formatDayOfWeek(props.day)}</DayOfWeekText>
              {showTimesLabel && knownDurationInHours !== null && (
                <VisitTimeLabel
                  dayOfWeek={props.day}
                  duration={knownDurationInHours}
                  startTime={props.startTime}
                  endTime={props.endTime}
                  isOvernight={isOvernight}
                />
              )}
              {showTimesLabel && startsToday && (
                <CloseVisitStartTimeLabel startTime={props.startTime} />
              )}
            </Flex>
            {props.areTimesFlexible ? null : (
              <FixedVisitTime
                startTime={props.startTime}
                duration={firstDuration}
                endTime={props.endTime}
              />
            )}
          </DayBlockHeader>
          <AnimatePresence initial={false}>
            {props.areTimesFlexible ? (
              <DayBlockSelection
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: { opacity: 1, height: "auto" },
                  collapsed: { opacity: 0, height: 0 },
                }}
              >
                <DurationContainer>
                  <HintText
                    isDurationSelected={isDurationSelected}
                    isOvernight={isOvernight}
                    numDurations={props.durations.length}
                    startTime={props.startTime}
                    endTime={props.endTime}
                  />
                  <Flex direction="row" xGap={2}>
                    {props.durations.map((duration) => {
                      const isDisabled = duration.isDisabled || props.isChecked === false;
                      return (
                        <HourChip
                          key={duration.numberOfHours}
                          isSelected={duration.isSelected ?? true}
                          isDisabled={isDisabled}
                          onClick={
                            isDisabled
                              ? undefined
                              : () => props.onSelectDuration(props.day, duration.numberOfHours)
                          }
                        >
                          {duration.numberOfHours}H
                        </HourChip>
                      );
                    })}
                  </Flex>
                </DurationContainer>
                <AvailableFlexibleTimeRangeSlider
                  showHelpAnimation={
                    durationInHours !== null &&
                    props.isSelectedTimeInAvailabilityRange &&
                    props.isChecked !== false &&
                    activateSliderHelpAnimation
                  }
                  minStartTime={minStartTime}
                  maxStartTime={LocalTime.of(23, 30)}
                  durationInHours={durationInHours ?? firstDuration}
                  isDisabled={durationInHours === null || props.isChecked === false}
                  value={props.startTime}
                  isSelectedTimeInAvailabilityRange={props.isSelectedTimeInAvailabilityRange}
                  isValidRange={props.isValidRange}
                  onChange={(value) => handleChangeStartTime(props.day, value)}
                />
              </DayBlockSelection>
            ) : null}
          </AnimatePresence>
        </DayBlocksContainer>
      }
    />
  );
}

function FixedVisitTime(props: { startTime: LocalTime; duration: number; endTime?: LocalTime }) {
  return (
    <FixedVisitTime.Styled>
      {formatTimes({
        startAt: props.startTime,
        endAt: props.endTime ?? props.startTime.plusMinutes(props.duration * 60),
      })}
    </FixedVisitTime.Styled>
  );
}

function VisitTimeLabel(props: {
  dayOfWeek: DayOfWeek;
  startTime: LocalTime;
  endTime?: LocalTime;
  isOvernight: boolean;
  duration: number;
}) {
  const endTime = props.endTime ?? props.startTime.plusMinutes(props.duration * 60);

  if (!props.isOvernight) {
    return (
      <VisitTimeLabel.Root>
        {formatTimes({ startAt: props.startTime, endAt: endTime })}
      </VisitTimeLabel.Root>
    );
  }

  return (
    <VisitTimeLabel.Root>
      {`${formatDayOfWeekAndTimeShort(
        props.dayOfWeek,
        props.startTime
      )} - ${formatDayOfWeekAndTimeShort(props.dayOfWeek.plus(1), endTime)}`}
    </VisitTimeLabel.Root>
  );
}

function HintText(props: {
  isDurationSelected: boolean;
  isOvernight: boolean;
  numDurations: number;
  startTime: LocalTime;
  endTime: LocalTime | undefined;
}) {
  const text = props.isDurationSelected
    ? translate(STRINGS.SELECT_START_TIME)
    : props.numDurations === 1
    ? translate(STRINGS.VISIT_DURATION)
    : translate(STRINGS.SELECT_VISIT_DURATION);

  return <HintText.Root>{text}</HintText.Root>;
}

HintText.Root = styled.span`
  font-size: 12px;
  color: var(--gray-500);
`;

VisitTimeLabel.Root = styled.span`
  flex: 1;
  text-align: end;
  color: #7b828d;
  font-size: 14px;
  text-align: start;
`;

const DayOfWeekText = styled.span`
  line-height: 24px;
`;

FixedVisitTime.Styled = styled.span`
  flex: 1;
  text-align: end;
  color: #7b828d;
`;

const DayBlocksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DayBlockHeader = styled.div<{ isDisabled: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 16px;
  opacity: ${(props) => (props.isDisabled ? 0.2 : 1)};
`;

const DayBlockSelection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DurationContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const HourChip = styled.span<{ isSelected: boolean; isDisabled: boolean }>`
  padding: 4px 10px;
  border: 2px solid ${(props) => (props.isSelected ? "var(--primary-color)" : "#F2F2F2")};
  border-radius: 12px;
  opacity: ${(props) => (props.isDisabled ? "0.2" : "1")};
`;

function CloseVisitStartTimeLabel(props: { startTime: LocalTime }) {
  const minutesDiff = LocalTime.now().until(props.startTime, ChronoUnit.MINUTES);
  const isInHourOrMore = minutesDiff > 59;
  const numHours = isInHourOrMore ? Math.floor(minutesDiff / 60) : minutesDiff;
  const unitString = translate(numHours === 1 ? STRINGS.HOUR : STRINGS.HOURS).toLowerCase();
  const label = `${numHours} ${unitString}`;

  return (
    <CloseVisitStartTimeLabelRoot>
      {isInHourOrMore
        ? translate(STRINGS.STARTS_TODAY_IN).replace("{{time}}", label)
        : translate(STRINGS.STARTS_NEXT_WEEK)}
    </CloseVisitStartTimeLabelRoot>
  );
}

const CloseVisitStartTimeLabelRoot = styled.span`
  color: var(--tw-blue-600);
  font-size: 14px;
`;

function formatDayOfWeek(dayOfWeek: DayOfWeek) {
  return globalize().formatDayOfWeek(dayOfWeek, false);
}

export default AvailableFlexibleShift;
